import {
  pgTable,
  pgEnum,
  text,
  serial,
  varchar,
  boolean,
  smallint,
  decimal,
  timestamp,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const stateStatusEnum = pgEnum("state_status", ["active", "inactive", "review"]);
export const ratingMethodEnum = pgEnum("rating_method", [
  "age",
  "composite",
  "community",
  "hybrid",
]);
export const ageCurveTypeEnum = pgEnum("age_curve_type", [
  "federal",
  "state_established",
]);
export const tobaccoRatingEnum = pgEnum("tobacco_rating", ["allowed", "prohibited"]);
export const exchangeTypeEnum = pgEnum("exchange_type", ["ffm", "sbe", "sbfe"]);

export const stateRules = pgTable(
  "state_rules",
  {
    id: serial("id").primaryKey(),
    stateCode: varchar("state_code", { length: 2 }).notNull().unique(),
    stateName: varchar("state_name", { length: 100 }).notNull(),
    status: stateStatusEnum("status").notNull().default("active"),

    smallGroupMin: smallint("small_group_min").notNull().default(1),
    smallGroupMax: smallint("small_group_max").notNull().default(50),

    ratingMethod: ratingMethodEnum("rating_method").notNull().default("age"),
    ageRatingRatio: decimal("age_rating_ratio", { precision: 4, scale: 2 })
      .notNull()
      .default("3.00"),
    ageCurveType: ageCurveTypeEnum("age_curve_type").notNull().default("federal"),
    tobaccoRating: tobaccoRatingEnum("tobacco_rating").notNull().default("allowed"),
    tobaccoRatioMax: decimal("tobacco_ratio_max", { precision: 4, scale: 2 }),
    stateSpecificCompositeMethod: boolean("state_specific_composite_method")
      .notNull()
      .default(false),

    uniformFamilyTiers: boolean("uniform_family_tiers").notNull().default(false),
    perMemberAllowed: boolean("per_member_allowed").notNull().default(true),
    compositeAllowed: boolean("composite_allowed").notNull().default(false),
    compositeRequired: boolean("composite_required").notNull().default(false),
    specialBillingLogic: text("special_billing_logic"),

    exchangeType: exchangeTypeEnum("exchange_type").notNull().default("ffm"),
    exchangeName: varchar("exchange_name", { length: 100 }),

    oepStartMonth: smallint("oep_start_month"),
    oepStartDay: smallint("oep_start_day"),
    oepEndMonth: smallint("oep_end_month"),
    oepEndDay: smallint("oep_end_day"),

    minParticipationPct: smallint("min_participation_pct"),
    minEmployerContributionPct: smallint("min_employer_contribution_pct"),
    participationWaiverAvailable: boolean("participation_waiver_available")
      .notNull()
      .default(false),
    participationNotes: text("participation_notes"),

    validationRules: text("validation_rules"),
    planLibraryBehavior: text("plan_library_behavior"),
    sourceTracking: text("source_tracking"),
    notes: text("notes"),

    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    stateCodeIdx: index("state_rules_state_code_idx").on(t.stateCode),
    statusIdx: index("state_rules_status_idx").on(t.status),
    stateCodeFormat: check(
      "state_rules_state_code_format",
      sql`${t.stateCode} ~ '^[A-Z]{2}$'`
    ),
    smallGroupRange: check(
      "state_rules_small_group_range",
      sql`${t.smallGroupMin} >= 1 AND ${t.smallGroupMax} <= 1000 AND ${t.smallGroupMin} <= ${t.smallGroupMax}`
    ),
    ageRatingRatioRange: check(
      "state_rules_age_rating_ratio_range",
      sql`${t.ageRatingRatio} >= 1.00 AND ${t.ageRatingRatio} <= 10.00`
    ),
    tobaccoRatioRange: check(
      "state_rules_tobacco_ratio_range",
      sql`${t.tobaccoRatioMax} IS NULL OR (${t.tobaccoRatioMax} >= 1.00 AND ${t.tobaccoRatioMax} <= 5.00)`
    ),
    participationPctRange: check(
      "state_rules_min_participation_pct_range",
      sql`${t.minParticipationPct} IS NULL OR (${t.minParticipationPct} >= 0 AND ${t.minParticipationPct} <= 100)`
    ),
    employerContributionPctRange: check(
      "state_rules_min_employer_contribution_pct_range",
      sql`${t.minEmployerContributionPct} IS NULL OR (${t.minEmployerContributionPct} >= 0 AND ${t.minEmployerContributionPct} <= 100)`
    ),
    oepStartMonthRange: check(
      "state_rules_oep_start_month_range",
      sql`${t.oepStartMonth} IS NULL OR (${t.oepStartMonth} >= 1 AND ${t.oepStartMonth} <= 12)`
    ),
    oepStartDayRange: check(
      "state_rules_oep_start_day_range",
      sql`${t.oepStartDay} IS NULL OR (${t.oepStartDay} >= 1 AND ${t.oepStartDay} <= 31)`
    ),
    oepEndMonthRange: check(
      "state_rules_oep_end_month_range",
      sql`${t.oepEndMonth} IS NULL OR (${t.oepEndMonth} >= 1 AND ${t.oepEndMonth} <= 12)`
    ),
    oepEndDayRange: check(
      "state_rules_oep_end_day_range",
      sql`${t.oepEndDay} IS NULL OR (${t.oepEndDay} >= 1 AND ${t.oepEndDay} <= 31)`
    ),
  })
);

export const insertStateRuleSchema = createInsertSchema(stateRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertStateRule = z.infer<typeof insertStateRuleSchema>;
export type StateRule = typeof stateRules.$inferSelect;
